/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useRef, useState} from "react";
import {service as UnitService} from "../../../Services/UnitService";
import {FormControl, InputGroup} from "react-bootstrap";
import {useFormattedValue} from "../../../Hooks/FormatterHooks";
import {usePrimeCache} from "../../../Hooks/ServiceHooks";

export function UnitNumberFieldEditor(props) {

	const inputElement = useRef();
	const {Value: value, FieldDef: fieldDef = {}} = props;
	const {customData = {}} = fieldDef;
	const {
		unitId = props?.unitId ?? -1,
		maximumFractionDigits = 0,
		minimumFractionDigits = 0,
		suffix = null,
	} = customData;
	
	usePrimeCache([UnitService]);

	const unit = UnitService.CachedValueById(unitId)?.result;
	const unsignedUnits = ['acre'];
	const canBeNegative = !unsignedUnits.includes(unit?.name);

	const formattedValue = useFormattedValue(value, {
		maximumFractionDigits,
		minimumFractionDigits,
		canBeNegative
	});

	const [previousSelectionStart, setPreviousSelectionStart] = useState(null)
	const [numberOfCommasBefore, setNumberOfCommasBefore] = useState((formattedValue ?? "").split(',').length)


	useEffect(() => {
		if (previousSelectionStart !== null) {
			const numberOfCommasAfter = formattedValue.split(',').length;
			const selectionStart = previousSelectionStart + (numberOfCommasAfter - numberOfCommasBefore); // adjust the cursor position if we added any commas
			inputElement.current?.setSelectionRange(selectionStart, selectionStart); // set the cursor position on setState callback handler
		}
	}, [formattedValue])

	const onChange = (event) => {
		const targetElement = event.target;
		// if the selection is at the end, don't change it, but otherwise record the cursor position
		setPreviousSelectionStart(targetElement.value.length === targetElement.selectionEnd ? null : targetElement.selectionStart);
		setNumberOfCommasBefore(targetElement.value.split(',').length);

		const cleanValue = targetElement.value.replace(/\,/g, '');
        const hasTrailingDecimal = cleanValue.endsWith('.');

		// Create a properly rounded number value that matches what's displayed
		let valueToUpdate = cleanValue;
		if (cleanValue !== "" && !isNaN(parseFloat(cleanValue))) {
			// Apply the same precision that's used in the formatter
			valueToUpdate = parseFloat(parseFloat(cleanValue).toFixed(maximumFractionDigits));
			
			// Ensure non-negative if needed
			if (!canBeNegative && valueToUpdate < 0) {
			    valueToUpdate = Math.abs(valueToUpdate);
			}
			
			// Preserve decimal point in the value sent to parent if needed
			if (hasTrailingDecimal) {
				valueToUpdate = valueToUpdate + '.';
			}
		}
		
		props.OnUpdate?.(String(valueToUpdate));
	}

	const isPrefix = unit?.isPrefix ?? false;
	const unitLabel = unit?.abbreviation ?? suffix ?? "";

	const formControl = <FormControl
		type="text"
		value={formattedValue}
		onChange={onChange}
		ref={inputElement}
	/>;

	// omit InputGroup to keep rounded corners when there is no label
	if (!unitLabel) {
		return formControl;
	}

	return <InputGroup>
		{!!isPrefix && <InputGroup.Append><InputGroup.Text>{unitLabel}</InputGroup.Text></InputGroup.Append>}
		{formControl}
		{!isPrefix && <InputGroup.Append><InputGroup.Text>{unitLabel}</InputGroup.Text></InputGroup.Append>}
	</InputGroup>;
}